import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from '@/store/store'
import useModal from '@/hooks/useModal'
import { selectOpenModalOutOfCredits, selectOpenModalTryPremium, selectUser, selectUserIsPremium } from '@/store/slices/user/selectors'
import useAppContext from '@/hooks/useAppContext'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { setWhatsNewData } from '@/store/slices/modal/action'
import { ShowModalType } from '@/scenes/engine/common/constants'
import api from '@/services/api'
import { isOlderThan24Hours } from '@/utils/isOlderThan24Hours'
import { getExperimentVariant } from '@/utils/experimentClient'
import { customAmplitude } from '@/utils/customAmplitude'
import { setShowedRemoveObjectTutorial } from '@/utils/tutorial'
import { buildPremiumPromptProperties, buildSignupAnalyticsContext } from '@/utils/signupPremiumAnalytics'
import { UserInfoStorage } from '@/utils/userInfoStorage'
import {
  PHOTOSHOOT_SOURCE_QUERY_PARAM,
  PHOTOSHOOT_SOURCE_VALUE,
  PHOTOSHOOT_TRANSFER_QUERY_PARAM,
} from '@/utils/indexedPhotoshoot'
import { PHOTOSHOOT_HANDOFF_ROUTE_PATH } from '@/utils/photoshootHandoffProtocol'
import { WHATSHOT_TRANSFER_QUERY_PARAM } from '@/utils/indexedWhatshot'
import { WHATSHOT_HANDOFF_ROUTE_PATH } from '@/utils/whatshotHandoffProtocol'

// Routes that own their own premium gating; no startup premium prompt there.
// `/create` covers the dashboard plus the `/create/ai-models` and `/create/photoshoots` gates.
const NO_STARTUP_PREMIUM_PROMPT_ROUTE_PREFIXES = ['/create', '/batch-studio']

export function isStartupPremiumPromptSuppressed(pathname: string): boolean {
  return NO_STARTUP_PREMIUM_PROMPT_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))
}

/**
 * Handles the startup modal decision tree when the user lands on an editor or create route.
 * Runs the startup flow once, then resumes the skipped premium prompt when
 * Create navigates into the editor. Other startup steps are not repeated.
 *
 * Decision tree:
 * - Skips modal flow when ob=0 URL param is present
 * - Shows SignIn if user is not logged in
 * - Shows Onboarding for first-time users (or skips via no-intro-slides experiment)
 * - Shows What's New for returning users with unseen items
 * - Falls back to premium prompt when applicable (never on create/batch routes)
 */
function useInitialModalFlow() {
  const dispatch = useAppDispatch()
  const { showModalType, setShowModalType } = useModal()
  const { isLoadedJsonSuccess } = useAppContext()
  const user = useSelector(selectUser)
  const userIsPremium = useSelector(selectUserIsPremium)
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)
  const openModalOutOfCredits = useSelector(selectOpenModalOutOfCredits)
  const location = useLocation()
  const previousPathRef = useRef(location.pathname)
  const pendingEditorEntryRef = useRef<'waiting-for-load' | 'waiting-for-ready' | null>(null)
  const [initialFlowSettled, setInitialFlowSettled] = useState(false)

  const isCreatePage = location.pathname.includes('create')

  const showModalTryPremiumIfNeed = useCallback(() => {
    if (userIsPremium || openModalTryPremium.opened || openModalTryPremium.isOpen || isStartupPremiumPromptSuppressed(location.pathname)) {
      return
    }

    const source = isCreatePage ? 'Create' : 'Editor Open'
    dispatch(
      setOpenModalTryPremium({
        isOpen: true,
        source: source,
        opened: true,
      })
    )

    const eventProperties = buildPremiumPromptProperties(
      buildSignupAnalyticsContext({
        source,
        type: 'Standard',
        pathname: location.pathname,
      }),
    )
    customAmplitude('Premium Prompt', eventProperties)
    // @ts-ignore
    window.dataLayer.push({ event: 'premium_prompt', ...eventProperties })
  }, [dispatch, isCreatePage, location.pathname, openModalTryPremium.opened, openModalTryPremium.isOpen, userIsPremium])

  useEffect(() => {
    const previousPath = previousPathRef.current
    previousPathRef.current = location.pathname
    const isEditorPath = location.pathname === '/' || /^\/design\/[^/]+\/edit\/?$/.test(location.pathname)
    if (!isEditorPath) {
      pendingEditorEntryRef.current = null
      return
    }
    if (previousPath.startsWith('/create')) pendingEditorEntryRef.current = 'waiting-for-load'
    if (!pendingEditorEntryRef.current) return
    if (!user || userIsPremium || openModalTryPremium.opened || openModalTryPremium.isOpen) {
      pendingEditorEntryRef.current = null
      return
    }
    // Create inherits `true`; only a loading cycle in this editor entry can release the prompt.
    if (!isLoadedJsonSuccess) {
      pendingEditorEntryRef.current = 'waiting-for-ready'
      return
    }
    if (pendingEditorEntryRef.current !== 'waiting-for-ready') return
    // The initial flow and editor import must finish before taking the modal slot.
    if (!initialFlowSettled || showModalType !== null || openModalOutOfCredits.isOpen) return
    pendingEditorEntryRef.current = null
    showModalTryPremiumIfNeed()
  }, [location.pathname, user, userIsPremium, openModalTryPremium.opened, openModalTryPremium.isOpen, initialFlowSettled, isLoadedJsonSuccess, showModalType, openModalOutOfCredits.isOpen, showModalTryPremiumIfNeed])

  async function handleShowModal() {
    if (!user) {
      setShowModalType(ShowModalType.SIGN_IN_1ST)
      return
    }

    let showedOnboarding = localStorage.getItem('showedOnboarding')
      ? JSON.parse(localStorage.getItem('showedOnboarding'))
      : false

    if (!showedOnboarding) {
      let isNoIntro = false
      try {
        const variant = await getExperimentVariant('no-intro-slides')
        isNoIntro = variant === 'no-intro-slides'
      } catch (error) {
        console.warn('Failed to fetch experiment variant, defaulting to standard flow:', error)
      }

      if (isNoIntro) {
        // Already logged in — safe to persist immediately
        localStorage.setItem('showedOnboarding', JSON.stringify(true))
        localStorage.setItem('showedOnboardingDate', new Date().toISOString())
        setShowedRemoveObjectTutorial()
        // Fall through to returning-user flow
      } else {
        setShowModalType(ShowModalType.ONBOARDING)
        return
      }
    }

    // Returning-user flow (also reached by isNoIntro + logged in)
    let showedOnboardingDate = localStorage.getItem('showedOnboardingDate')
      ? localStorage.getItem('showedOnboardingDate')
      : null
    if (showedOnboardingDate && !isOlderThan24Hours(showedOnboardingDate)) {
      showModalTryPremiumIfNeed()
      // @ts-ignore
      window.dataLayer.push({ event: 'recurring_user' })
      return
    }

    let dataWhatnew = await api.getWhatsNew().catch(() => null)
    let whatNewShowedIds: string[] = localStorage.getItem('whatNewIds')
      ? JSON.parse(localStorage.getItem('whatNewIds'))
      : []

    let firstUnshowedItem = null
    if (dataWhatnew?.objects) {
      firstUnshowedItem = dataWhatnew.objects.find((item: any) => !whatNewShowedIds.includes(item.id))
    }

    if (firstUnshowedItem) {
      localStorage.setItem('whatNewIds', JSON.stringify([firstUnshowedItem.id, ...whatNewShowedIds]))
      dispatch(setWhatsNewData([firstUnshowedItem]))
      setShowModalType(ShowModalType.WHAT_NEW)
      customAmplitude('Whats New Prompt', {
        Source: 'Updated',
        ID: firstUnshowedItem.id,
      })
    } else if (dataWhatnew?.objects && dataWhatnew.objects.length > 0) {
      // No unshowed items — show most recent What's New item
      dispatch(setWhatsNewData(dataWhatnew?.objects?.slice(0, 1) ?? []))
      showModalTryPremiumIfNeed()
    } else {
      showModalTryPremiumIfNeed()
    }
  }

  useEffect(() => {
    if (
      location.pathname.startsWith(PHOTOSHOOT_HANDOFF_ROUTE_PATH) ||
      location.pathname.startsWith(WHATSHOT_HANDOFF_ROUTE_PATH)
    ) {
      // The handoff owns startup UI, but a later Create upload still owns its prompt.
      setInitialFlowSettled(true)
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has(WHATSHOT_TRANSFER_QUERY_PARAM)) {
      // Batch Studio owns guest gating and resumes this one-time transfer after login.
      setInitialFlowSettled(true)
      return
    }
    const ob = urlParams.get('ob')
    if (ob === '0') {
      setShowedRemoveObjectTutorial()
      return
    }

    const ssoCode = urlParams.get('sso_code')
    const photoshootTransfer = urlParams.get(PHOTOSHOOT_TRANSFER_QUERY_PARAM)
    const fromPhotoshoot = urlParams.get(PHOTOSHOOT_SOURCE_QUERY_PARAM) === PHOTOSHOOT_SOURCE_VALUE
    const isPhotoshootSsoLanding = !!ssoCode || !!photoshootTransfer || fromPhotoshoot

    if (isPhotoshootSsoLanding) {
      setInitialFlowSettled(true)
      // Redeem-in-flight path: `/sso` owns the success/failure decision.
      if (ssoCode) return
      // No-code bridge landing should still gate guests behind signup.
      if (!UserInfoStorage.get()) {
        setShowModalType(ShowModalType.SIGN_IN_1ST)
      }
      return
    }

    let cancelled = false
    void handleShowModal().finally(() => {
      if (!cancelled) setInitialFlowSettled(true)
    })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}

export default useInitialModalFlow
